import { supabase } from '../lib/supabase';

export const subscriptionService = {
  /**
   * Get all subscription plans
   */
  async getSubscriptionPlans() {
    try {
      const { data, error } = await supabase
        ?.from('subscription_plans')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('price_monthly', { ascending: true });

      if (error) throw error;

      // Convert snake_case to camelCase
      const plans = data?.map(plan => ({
        id: plan?.id,
        name: plan?.name,
        description: plan?.description,
        priceMonthly: plan?.price_monthly,
        priceYearly: plan?.price_yearly,
        currency: plan?.currency,
        stripePriceId: plan?.stripe_price_id,
        stripeProductId: plan?.stripe_product_id,
        features: plan?.features || [],
        seatLimit: plan?.seat_limit,
        sessionLimit: plan?.session_limit,
        isActive: plan?.is_active,
        createdAt: plan?.created_at,
        updatedAt: plan?.updated_at
      })) || [];

      return { data: plans, error: null };
    } catch (error) {
      console.error('Get subscription plans error:', error);
      return { data: [], error };
    }
  },

  /**
   * Get organization's current subscription
   */
  async getOrganizationSubscription(organizationId) {
    try {
      const { data, error } = await supabase
        ?.from('organization_subscriptions')
        ?.select(`
          *,
          subscription_plans (*)
        `)
        ?.eq('organization_id', organizationId)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      if (!data) {
        return { data: null, error: null };
      }

      // Convert snake_case to camelCase
      const subscription = {
        id: data?.id,
        organizationId: data?.organization_id,
        planId: data?.plan_id,
        stripeSubscriptionId: data?.stripe_subscription_id,
        stripeCustomerId: data?.stripe_customer_id,
        status: data?.status,
        billingCycle: data?.billing_cycle,
        currentPeriodStart: data?.current_period_start,
        currentPeriodEnd: data?.current_period_end,
        cancelAtPeriodEnd: data?.cancel_at_period_end,
        seatsUsed: data?.seats_used,
        sessionsUsed: data?.sessions_used,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        plan: data?.subscription_plans ? {
          id: data?.subscription_plans?.id,
          name: data?.subscription_plans?.name,
          description: data?.subscription_plans?.description,
          priceMonthly: data?.subscription_plans?.price_monthly,
          priceYearly: data?.subscription_plans?.price_yearly,
          currency: data?.subscription_plans?.currency,
          features: data?.subscription_plans?.features || [],
          seatLimit: data?.subscription_plans?.seat_limit,
          sessionLimit: data?.subscription_plans?.session_limit
        } : null
      };

      return { data: subscription, error: null };
    } catch (error) {
      console.error('Get organization subscription error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get organization invoices
   */
  async getOrganizationInvoices(organizationId, limit = 10) {
    try {
      const { data, error } = await supabase
        ?.from('invoices')
        ?.select('*')
        ?.eq('organization_id', organizationId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      // Convert snake_case to camelCase
      const invoices = data?.map(invoice => ({
        id: invoice?.id,
        organizationId: invoice?.organization_id,
        subscriptionId: invoice?.subscription_id,
        stripeInvoiceId: invoice?.stripe_invoice_id,
        invoiceNumber: invoice?.invoice_number,
        amountDue: invoice?.amount_due,
        amountPaid: invoice?.amount_paid,
        currency: invoice?.currency,
        status: invoice?.status,
        invoicePdf: invoice?.invoice_pdf,
        hostedInvoiceUrl: invoice?.hosted_invoice_url,
        billingReason: invoice?.billing_reason,
        periodStart: invoice?.period_start,
        periodEnd: invoice?.period_end,
        dueDate: invoice?.due_date,
        paidAt: invoice?.paid_at,
        createdAt: invoice?.created_at
      })) || [];

      return { data: invoices, error: null };
    } catch (error) {
      console.error('Get organization invoices error:', error);
      return { data: [], error };
    }
  },

  /**
   * Get usage metrics for organization
   */
  async getUsageMetrics(organizationId, days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase
        ?.from('subscription_usage_metrics')
        ?.select('*')
        ?.eq('organization_id', organizationId)
        ?.gte('metric_date', startDate?.toISOString()?.split('T')?.[0])
        ?.order('metric_date', { ascending: true });

      if (error) throw error;

      // Convert snake_case to camelCase
      const metrics = data?.map(metric => ({
        id: metric?.id,
        organizationId: metric?.organization_id,
        subscriptionId: metric?.subscription_id,
        metricDate: metric?.metric_date,
        activeUsers: metric?.active_users,
        sessionsCompleted: metric?.sessions_completed,
        helpersActive: metric?.helpers_active,
        costPerUser: metric?.cost_per_user,
        createdAt: metric?.created_at
      })) || [];

      return { data: metrics, error: null };
    } catch (error) {
      console.error('Get usage metrics error:', error);
      return { data: [], error };
    }
  },

  /**
   * Get payment methods for organization
   */
  async getPaymentMethods(organizationId) {
    try {
      const { data, error } = await supabase
        ?.from('payment_methods')
        ?.select('*')
        ?.eq('organization_id', organizationId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase
      const paymentMethods = data?.map(method => ({
        id: method?.id,
        organizationId: method?.organization_id,
        stripePaymentMethodId: method?.stripe_payment_method_id,
        type: method?.type,
        cardBrand: method?.card_brand,
        cardLastFour: method?.card_last_four,
        cardExpMonth: method?.card_exp_month,
        cardExpYear: method?.card_exp_year,
        isDefault: method?.is_default,
        createdAt: method?.created_at
      })) || [];

      return { data: paymentMethods, error: null };
    } catch (error) {
      console.error('Get payment methods error:', error);
      return { data: [], error };
    }
  },

  /**
   * Create checkout session (calls Edge Function)
   */
  async createCheckoutSession(planId, billingCycle, organizationId) {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-checkout-session', {
        body: {
          planId,
          billingCycle,
          organizationId
        }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Create checkout session error:', error);
      return { data: null, error };
    }
  },

  /**
   * Create billing portal session (calls Edge Function)
   */
  async createBillingPortalSession(organizationId) {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-billing-portal-session', {
        body: { organizationId }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Create billing portal session error:', error);
      return { data: null, error };
    }
  },

  /**
   * Cancel subscription
   */
  async cancelSubscription(subscriptionId) {
    try {
      const { data, error } = await supabase?.functions?.invoke('cancel-subscription', {
        body: { subscriptionId }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Cancel subscription error:', error);
      return { data: null, error };
    }
  },

  /**
   * Format currency amount
   */
  formatAmount(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    })?.format(amount);
  },

  /**
   * Calculate usage percentage
   */
  calculateUsagePercentage(used, limit) {
    if (limit === -1) return 0; // Unlimited
    if (limit === 0) return 100;
    return Math.min(Math.round((used / limit) * 100), 100);
  }
};